import { put, call, takeEvery } from "redux-saga/effects";

import * as apis from "../../network/apis/requests";
import * as types from "./types";
import * as actions from "./actions";
import { getEventRequest } from "../Events/actions";
import {
  showErrorMsg,
  showSuccessMsg,
  showWarningMsg,
} from "../../utils/Helpers";

const normalizeIds = (ids = []) =>
  ids.map((id) => {
    const numberId = Number(id);
    return Number.isFinite(numberId) ? numberId : id;
  });

const isBulkOperationPartialSuccess = (response) => response?.status === 207;

function* refreshEventRequestList(listParams) {
  if (!listParams) return;
  yield put(actions.getEventRequestRequest(listParams));
}

function* refreshRequestsList(listParams) {
  if (!listParams) return;
  if (listParams.id && listParams.params) {
    yield put(actions.getEventRequestRequest(listParams));
    return;
  }
  yield put(actions.getRequestsRequest(listParams));
}

function* handleBulkResponse(response, fallbackSuccessKey, successToast) {
  const successMessage = response?.data?.message;
  if (isBulkOperationPartialSuccess(response)) {
    yield showWarningMsg(successMessage);
    return false;
  }
  if (successToast) {
    yield call(showSuccessMsg, successToast.message, true, successToast.title);
  } else if (successMessage) {
    yield showSuccessMsg(successMessage, true);
  } else if (fallbackSuccessKey) {
    yield showSuccessMsg(fallbackSuccessKey);
  }
  return true;
}

function* getRequestsListSaga({ payload }) {
  try {
    let actionType = {
      requests: "getRequestsResponse",
      zoneApplicants: "getzoneApplicantsResponse",
      applicantsTab: "getApplicantsTabResponse",
      unassignApplicants: "getUnassignApplicantsResponse",
    };
    yield put(
      actions[actionType[payload["responseType"]]]({
        list: [],
        meta: {},
      }),
    );
    const response = yield call(apis.getRequestsList, payload);
    yield put(
      actions[actionType[payload["responseType"]]]({
        list: response.data.data,
        meta: {
          currentPage: +response.headers["current-page"],
          count: +response.headers["total-count"],
          pages: +response.headers["total-pages"],
        },
      }),
    );
    if (+response.headers["current-page"] > +response.headers["total-pages"]) {
      yield put(
        actions.getRequestsRequest({
          ...payload,
          page: +response.headers["total-pages"],
        }),
      );
    }
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
  }
}

function* approveRequestSaga({ payload }) {
  try {
    const { data, id, listParams } = payload;
    const response = yield call(apis.bulkApproveAdminEventRequests, {
      event_request_ids: normalizeIds([id]),
      ...(data?.role_id ? { role_id: data.role_id } : {}),
      ...(data?.event_role_shift_id
        ? { event_role_shift_id: data.event_role_shift_id }
        : {}),
    });
    yield call(handleBulkResponse, response, "approveRequest");
    yield call(refreshRequestsList, listParams);
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
  }
}

function* updateEventRequestSaga({ payload }) {
  try {
    const { data, id, listParams, successToast, onSuccess } = payload;
    const eventRequestIds = normalizeIds([id]);
    let response;

    if (data?.role_id != null && data?.event_role_shift_id != null) {
      response = yield call(apis.bulkChangeRoleAdminEventRequests, {
        event_request_ids: eventRequestIds,
        role_id: data.role_id,
        event_role_shift_id: data.event_role_shift_id,
      });
    } else if (data?.zone_id != null) {
      response = yield call(apis.bulkAllocateAdminEventRequests, {
        event_request_ids: eventRequestIds,
        zone_id: data.zone_id,
      });
    } else if (Object.prototype.hasOwnProperty.call(data, "zone_id")) {
      response = yield call(apis.bulkUnassignAdminEventRequests, {
        event_request_ids: eventRequestIds,
      });
    } else {
      return;
    }

    yield call(handleBulkResponse, response, "updatedSuccessfully", successToast);
    yield call(refreshEventRequestList, listParams);
    if (listParams?.event_id) {
      yield put(getEventRequest(listParams.event_id));
    }
    onSuccess?.();
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
  }
}

function* removeEventRequestSaga({ payload }) {
  try {
    const response = yield call(apis.bulkRemoveAdminEventRequests, {
      event_request_ids: normalizeIds([payload.id]),
    });
    yield call(handleBulkResponse, response);
    yield put(actions.removeEventResponse(response.data));
    yield call(refreshEventRequestList, payload.listParams);
    if (payload.listParams?.event_id) {
      yield put(getEventRequest(payload.listParams.event_id));
    }
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
  }
}

function* updateSupervisorEventRequestSaga({ payload }) {
  try {
    yield call(apis.updateSupervisorEventRequest, payload);
    if (payload.successToast) {
      yield call(
        showSuccessMsg,
        payload.successToast.message,
        true,
        payload.successToast.title
      );
    } else {
      yield showSuccessMsg("updatedSuccessfully");
    }
    yield call(refreshEventRequestList, payload.listParams);
    if (payload.listParams?.event_id) {
      yield put(getEventRequest(payload.listParams.event_id));
    }
    payload.onSuccess?.();
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
  }
}

function* bulkAssignSaga({ payload }) {
  try {
    const response = yield call(apis.bulkAllocateAdminEventRequests, {
      zone_id: payload.data.zone_id,
      event_request_ids: normalizeIds(payload.data.event_request_ids),
    });
    yield call(
      handleBulkResponse,
      response,
      "updatedSuccessfully",
      payload.successToast
    );
    yield call(refreshEventRequestList, payload.listParams);
    if (payload.listParams?.event_id) {
      yield put(getEventRequest(payload.listParams.event_id));
    }
    payload.onSuccess?.();
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
    payload.onError?.();
  }
}

function* getEventRequestListSaga({ payload }) {
  try {
    const response = yield call(apis.getEventRequestList, payload);
    yield put(
      actions.getEventRequestResponse({
        list: response.data.data,
        meta: {
          currentPage: +response.headers["current-page"],
          count: +response.headers["total-count"],
          pages: +response.headers["total-pages"],
        },
      }),
    );
    if (+response.headers["current-page"] > +response.headers["total-pages"]) {
      yield put(
        actions.getEventRequestRequest({
          ...payload,
          page: +response.headers["total-pages"],
        }),
      );
    }
  } catch (error) {
    yield error.response && showErrorMsg(error.response?.data?.message);
  }
}

function* updateBulkApprovedRequestSaga({ payload }) {
  try {
    const response = yield call(apis.bulkApproveAdminEventRequests, {
      event_request_ids: normalizeIds(payload.event_request_ids),
      ...(payload.role_id ? { role_id: payload.role_id } : {}),
      ...(payload.event_role_shift_id
        ? { event_role_shift_id: payload.event_role_shift_id }
        : {}),
    });
    yield call(handleBulkResponse, response);
    yield call(refreshEventRequestList, payload.listParams);
  } catch (error) {
    yield error.response && showErrorMsg(error.response?.data?.message);
  }
}

export default function* RequestsSagas() {
  yield takeEvery(types.GET_REQUESTS_REQUEST, getRequestsListSaga);
  yield takeEvery(types.APPROVE_REQUEST, approveRequestSaga);
  yield takeEvery(types.UPDATE_EVENT_REQUEST, updateEventRequestSaga);
  yield takeEvery(types.REMOVE_EVENT_REQUEST, removeEventRequestSaga);
  yield takeEvery(
    types.UPDATE_SUPERVISOR_EVENT_REQUEST,
    updateSupervisorEventRequestSaga,
  );
  yield takeEvery(types.BULK_ASSIGN, bulkAssignSaga);
  yield takeEvery(types.GET_EVENT_REQUEST_REQUEST, getEventRequestListSaga);
  yield takeEvery(
    types.BULK_APPROVED_REQUESTS_REQUEST,
    updateBulkApprovedRequestSaga,
  );
}
